import type { Session } from "@supabase/supabase-js";
import { Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../lib/config";
import { AdminInvitationComposer, createInvitationDraft, type InvitationDraft } from "./AdminInvitationComposer";

function currentInvitationTab() {
  return new URLSearchParams(window.location.search).get("tab") === "invitations";
}

function replaceAdminTab(tab?: "bookings" | "challenges" | "invitations" | "settings") {
  const url = new URL(window.location.href);
  url.searchParams.set("view", "admin");
  if (tab) url.searchParams.set("tab", tab);
  else url.searchParams.delete("tab");
  url.hash = "";
  window.history.replaceState({}, "", url);
}

function tabFromButton(button: HTMLButtonElement) {
  const label = button.textContent ?? "";
  if (label.includes("Terminanfragen")) return "bookings" as const;
  if (label.includes("Challenges")) return "challenges" as const;
  if (label.includes("Einstellungen")) return "settings" as const;
  return undefined;
}

export function AdminInvitationIntegration() {
  const [session, setSession] = useState<Session | null>(null);
  const [navSlot, setNavSlot] = useState<HTMLElement | null>(null);
  const [pageSlot, setPageSlot] = useState<HTMLElement | null>(null);
  const [active, setActive] = useState(currentInvitationTab);
  const [draft, setDraft] = useState<InvitationDraft>(createInvitationDraft);

  useEffect(() => {
    if (!supabase) return;
    void supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) return;

    let navHost: HTMLElement | null = null;
    let contentHost: HTMLElement | null = null;
    let observer: MutationObserver | null = null;
    let navElement: HTMLElement | null = null;
    const hiddenElements: Array<{ element: HTMLElement; display: string }> = [];

    const handleNavigation = (event: Event) => {
      const target = event.target as HTMLElement | null;
      const button = target?.closest("button") as HTMLButtonElement | null;
      if (!button || !navElement?.contains(button) || navHost?.contains(button)) return;

      const nextTab = tabFromButton(button);
      if (nextTab) replaceAdminTab(nextTab);
      setActive(false);
    };

    const attach = () => {
      const nav = document.querySelector<HTMLElement>(".admin-sidebar nav");
      const main = document.querySelector<HTMLElement>(".admin-main");
      const topbar = main?.querySelector<HTMLElement>(".admin-topbar");
      if (!nav || !main || !topbar) return false;

      navElement = nav;
      nav.addEventListener("click", handleNavigation);

      navHost = document.createElement("span");
      navHost.style.display = "contents";
      navHost.dataset.adminInvitationNav = "true";
      const settingsButton = Array.from(nav.children).find((item) => item.textContent?.includes("Einstellungen"));
      nav.insertBefore(navHost, settingsButton ?? null);
      setNavSlot(navHost);

      if (active) {
        const heading = topbar.querySelector("h1");
        if (heading) heading.textContent = "Einladungen";

        contentHost = document.createElement("div");
        contentHost.dataset.adminInvitationPage = "true";
        topbar.insertAdjacentElement("afterend", contentHost);
        setPageSlot(contentHost);

        for (const child of Array.from(main.children)) {
          if (!(child instanceof HTMLElement) || child === topbar || child === contentHost) continue;
          hiddenElements.push({ element: child, display: child.style.display });
          child.style.display = "none";
        }
      }
      return true;
    };

    if (!attach()) {
      observer = new MutationObserver(() => {
        if (attach()) observer?.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      observer?.disconnect();
      navElement?.removeEventListener("click", handleNavigation);
      for (const { element, display } of hiddenElements) element.style.display = display;
      navHost?.remove();
      contentHost?.remove();
      setNavSlot(null);
      setPageSlot(null);
    };
  }, [active, session]);

  if (!session || !navSlot) return null;

  return (
    <>
      {createPortal(
        <button
          type="button"
          className={active ? "active" : ""}
          onClick={() => {
            replaceAdminTab("invitations");
            setActive(true);
          }}
        >
          <Mail /> Einladungen
        </button>,
        navSlot,
      )}
      {active && pageSlot && createPortal(<AdminInvitationComposer session={session} draft={draft} setDraft={setDraft} />, pageSlot)}
    </>
  );
}
